import { BlogListSeo } from "@/components/blog/BlogSeo";
import { PostCard } from "@/components/blog/PostCard";
import {
  Alert,
  Button,
  EmptyState,
  Heading,
  Pagination,
  SearchField,
  Skeleton,
  Text,
} from "@/design-system";
import { useBlogCategories, usePublishedPosts } from "@/hooks/blog";
import {
  Footer,
  Header,
  WhatsAppFloatButton,
} from "@/pages/landingPage/components";
import { DEFAULT_ORIGIN } from "@/shared/blog/seo";
import { useSearchParams } from "react-router";

const PAGE_SIZE = 9;

export const BlogListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? 1) || 1);
  const category = searchParams.get("categoria") ?? undefined;
  const search = searchParams.get("busca") ?? undefined;

  const { data, isLoading, isError, refetch, isFetching } = usePublishedPosts({
    page,
    pageSize: PAGE_SIZE,
    category,
    search,
  });
  const { data: categories = [] } = useBlogCategories();

  const canonical =
    page > 1 ? `${DEFAULT_ORIGIN}/blog?page=${page}` : `${DEFAULT_ORIGIN}/blog`;

  const updateParams = (next: {
    page?: number;
    categoria?: string | null;
    busca?: string;
  }) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (next.busca !== undefined) {
        if (next.busca) params.set("busca", next.busca);
        else params.delete("busca");
        params.delete("page");
      }
      if (next.categoria !== undefined) {
        if (next.categoria) params.set("categoria", next.categoria);
        else params.delete("categoria");
        params.delete("page");
      }
      if (next.page !== undefined) {
        if (next.page > 1) params.set("page", String(next.page));
        else params.delete("page");
      }
      return params;
    });
  };

  const posts = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <>
      <BlogListSeo canonical={canonical} />
      <div className="bg-ds-paper min-h-screen">
        <Header />

        <main className="mx-auto w-full max-w-6xl px-4 pt-28 pb-16 sm:px-6">
          <header className="mx-auto max-w-3xl text-center">
            <Heading level={1}>Blog da Fidexa</Heading>
            <Text size="lead" tone="muted" className="mt-4">
              Estratégias práticas de gestão de crediário, cobrança via WhatsApp
              e redução de inadimplência para o varejo brasileiro.
            </Text>
          </header>

          {/* A busca é o recorte por texto do acervo; fica na URL (`busca`)
              como a categoria, para o link compartilhado abrir o mesmo
              resultado. */}
          <SearchField
            alwaysOpen
            label="Buscar no blog"
            placeholder="Buscar por assunto"
            defaultValue={search}
            onSearch={termo => updateParams({ busca: termo.trim() })}
            className="mx-auto mt-8 max-w-2xl"
          />

          {/*
            Categoria aqui não é filtro de `DataList`: é navegação pública (muda
            a URL e é o recorte do acervo que o leitor escolhe), então fica à
            vista em vez de dentro de um `Modal`. Cada cápsula é um `Button` do
            sistema, com os 44px de alvo e `aria-pressed` dizendo o que está
            valendo — `Badge` não serve porque é um `<span>`, e categoria clicável
            precisa ser botão de verdade.
          */}
          {categories.length > 0 && (
            <nav
              aria-label="Filtrar por categoria"
              className="mt-8 flex flex-wrap justify-center gap-2"
            >
              <Button
                type="button"
                size="sm"
                variant={!category ? "primary" : "secondary"}
                aria-pressed={!category}
                onClick={() => updateParams({ categoria: null })}
                className="w-auto rounded-full"
              >
                Todos
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat.slug}
                  type="button"
                  size="sm"
                  variant={category === cat.name ? "primary" : "secondary"}
                  aria-pressed={category === cat.name}
                  onClick={() => updateParams({ categoria: cat.name })}
                  className="w-auto rounded-full"
                >
                  {cat.name}
                </Button>
              ))}
            </nav>
          )}

          <section aria-labelledby="lista-de-posts" className="mt-10">
            <Heading level={2} size={3} id="lista-de-posts" className="sr-only">
              Últimos conteúdos
            </Heading>

            {isLoading ? (
              <div
                role="status"
                aria-busy="true"
                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                <span className="sr-only">Carregando os conteúdos do blog</span>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-3">
                    <Skeleton className="rounded-ds-md aspect-[16/5] w-full" />
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-5 w-4/5" />
                  </div>
                ))}
              </div>
            ) : isError ? (
              <Alert
                tone="danger"
                title="Não foi possível carregar os conteúdos"
              >
                <div className="flex flex-col items-start gap-3">
                  <Text size="small" tone="muted">
                    Verifique sua conexão e tente de novo.
                  </Text>
                  <Button
                    variant="secondary"
                    isLoading={isFetching}
                    loadingLabel="Carregando os conteúdos"
                    onClick={() => refetch()}
                  >
                    Tentar de novo
                  </Button>
                </div>
              </Alert>
            ) : posts.length === 0 ? (
              <EmptyState
                title={
                  search
                    ? `Nenhum conteúdo encontrado para “${search}”`
                    : category
                      ? `Nenhum conteúdo em “${category}” por enquanto`
                      : "Em breve, conteúdos sobre crediário e cobrança"
                }
                description={
                  search
                    ? "Tente outra palavra, ou limpe a busca no x do campo."
                    : category
                      ? "Veja o acervo completo enquanto preparamos os materiais desta categoria."
                      : "Estamos preparando os primeiros artigos. Volte em breve."
                }
                action={
                  // Sem "Limpar busca" aqui: o texto continuaria no campo,
                  // que só lê o `defaultValue` ao montar.
                  !search && category ? (
                    <Button
                      variant="secondary"
                      onClick={() => updateParams({ categoria: null })}
                    >
                      Ver todas as categorias
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {posts.map(post => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <Pagination
                    className="mt-12"
                    page={page}
                    totalPages={totalPages}
                    disabled={isFetching}
                    label="Paginação dos conteúdos do blog"
                    onPageChange={next => updateParams({ page: next })}
                  />
                )}
              </>
            )}
          </section>
        </main>

        <Footer />
        <WhatsAppFloatButton />
      </div>
    </>
  );
};
